import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import Network from '@/Network'
import { getApiErrorMessage, getApiErrorStatus, hasApiErrorCode } from '@/utils/apiError'
import {
  getQualificationErrorCode,
  getQualificationMissing,
  isProfessionalDocumentsDisabled
} from '@/utils/qualificationErrors'
import {
  shouldRequestMove,
  sortPreAllocationCards
} from '@/modules/company/Approvals/ProfessionalDocuments/model'
import { KANBAN_LOAD_ERROR } from '@/modules/company/Approvals/ProfessionalDocuments/constants'
import {
  PreAllocationColumn,
  QualificationErrorCode
} from '@/types/qualification-eligibility.types'
import type { EligibilityReason, PreAllocationCard } from '@/types/qualification-eligibility.types'

/** Cartão sumiu (cancelado, fora do escopo ou inexistente): 404 sem revelar qual. */
const PRE_ALLOCATION_NOT_FOUND = 'PRE_ALLOCATION_NOT_FOUND'

export type MoveOutcome =
  | { kind: 'noop' }
  | { kind: 'refreshed' }
  | { kind: 'unavailable' }
  /** `missing` vazio = backend sem a lista (antigo): a tela mostra o texto de fallback. */
  | { kind: 'blocked'; missing: EligibilityReason[] }

/** Demandas urgentes (D38): a coluna vem do backend; mover só pergunta e explica (D43). */
export const usePreAllocationKanbanStore = defineStore('preAllocationKanban', () => {
  const cards = ref<PreAllocationCard[]>([])
  const isLoading = ref(false)
  const isLoaded = ref(false)
  /** Backend com a flag desligada (404 `PROFESSIONAL_DOCUMENTS_DISABLED`). */
  const isUnavailable = ref(false)
  /** 403 do `@AccessibleResources`: o usuário não tem escala com revisão de documentos (A8). */
  const hasNoScope = ref(false)
  const loadError = ref<string | null>(null)
  const hasError = computed(() => loadError.value !== null)
  const movingId = ref<number | null>(null)
  let fetchId = 0

  const sortedCards = computed(() => sortPreAllocationCards(cards.value))
  const openCount = computed(
    () => cards.value.filter((card) => card.column !== PreAllocationColumn.RESOLVED).length
  )

  async function fetch(options: { silent?: boolean } = {}): Promise<void> {
    const current = ++fetchId
    if (!options.silent) isLoading.value = true
    loadError.value = null
    try {
      const data = await Network.preAllocations.list()
      if (current !== fetchId) return
      cards.value = data
      isLoaded.value = true
      isUnavailable.value = false
      hasNoScope.value = false
    } catch (error) {
      if (current !== fetchId) return
      // fallback intencional (nos dois casos): não é erro de tela; o Kanban mostra o estado
      // "indisponível" ou "sem escala acessível" no lugar dos cartões, sem toast.
      isUnavailable.value = isProfessionalDocumentsDisabled(error)
      hasNoScope.value = getApiErrorStatus(error) === 403
      if (isUnavailable.value || hasNoScope.value) return
      console.error(error)
      loadError.value = getApiErrorMessage(error, KANBAN_LOAD_ERROR)
    } finally {
      if (current === fetchId) isLoading.value = false
    }
  }

  /**
   * Relança o que não for bloqueio nem "indisponível"; com 404 do cartão a lista é recarregada
   * antes (o cartão some) e quem chama mostra a mensagem do backend.
   */
  async function move(card: PreAllocationCard, target: PreAllocationColumn): Promise<MoveOutcome> {
    if (movingId.value !== null || !shouldRequestMove(card, target)) return { kind: 'noop' }
    movingId.value = card.id
    try {
      await Network.preAllocations.move(card.id, target)
      await fetch({ silent: true })
      return { kind: 'refreshed' }
    } catch (error) {
      const code = getQualificationErrorCode(error)
      if (code === QualificationErrorCode.PRE_ALLOCATION_HAS_PENDING_ITEMS) {
        return { kind: 'blocked', missing: getQualificationMissing(error) }
      }
      if (isProfessionalDocumentsDisabled(error)) {
        isUnavailable.value = true
        return { kind: 'unavailable' }
      }
      if (hasApiErrorCode(error, PRE_ALLOCATION_NOT_FOUND)) await fetch({ silent: true })
      throw error
    } finally {
      movingId.value = null
    }
  }

  function $reset() {
    fetchId++
    cards.value = []
    isLoading.value = false
    isLoaded.value = false
    isUnavailable.value = false
    hasNoScope.value = false
    loadError.value = null
    movingId.value = null
  }

  return {
    cards,
    sortedCards,
    openCount,
    isLoading,
    isLoaded,
    isUnavailable,
    hasNoScope,
    loadError,
    hasError,
    movingId,
    fetch,
    move,
    $reset
  }
})
